/**
 * PlayersPortalCard — rectangle on the Club Admin Dashboard (below the
 * tables grid) that opens the full Players Portal modal. Shows a tiny
 * summary count of registered players.
 *
 * Listens for a global `portal:refresh` window event so it auto-refetches
 * whenever a payment / blacklist / damage / new-player change happens
 * elsewhere in the app (UnpaidSessionsPanel, StopSessionModal, …).
 */
import { useEffect, useState, useCallback } from "react";
import { Users, ChevronRight, Fingerprint } from "lucide-react";
import { api } from "@/lib/api";
import PlayersPortalModal from "@/components/PlayersPortalModal";

export const PORTAL_REFRESH_EVENT = "portal:refresh";

export default function PlayersPortalCard() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(null);
  const [blacklisted, setBlacklisted] = useState(0);
  const [unpaidTotal, setUnpaidTotal] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/portal/players", { params: { limit: 1000 } });
      setCount(data.length);
      setBlacklisted(data.filter((p) => p.is_blacklisted).length);
      setUnpaidTotal(
        data.reduce((s, p) => s + (Number(p.unpaid_session_total) || 0), 0),
      );
    } catch {
      setCount(0);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Live updates from anywhere else in the app
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener(PORTAL_REFRESH_EVENT, handler);
    return () => window.removeEventListener(PORTAL_REFRESH_EVENT, handler);
  }, [refresh]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="players-portal-card"
        className="mt-6 w-full text-left rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-900/30 via-zinc-900/60 to-zinc-900/60 hover:border-emerald-400/40 transition px-5 py-4 flex items-center justify-between group"
      >
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-emerald-500/15 grid place-items-center text-emerald-300">
            <Fingerprint className="h-6 w-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-emerald-100 tracking-wide uppercase">
                Players Portal
              </h3>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/20">
                CROSS-CLUB
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              Register players with photo + phone, get a unique code & QR, track history across clubs, manage blacklists.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right hidden sm:block">
            <div className="text-2xl font-bold text-emerald-200 tabular-nums" data-testid="portal-count">
              {count === null ? "—" : count}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500">Registered</div>
          </div>
          {blacklisted > 0 && (
            <div className="text-right hidden sm:block">
              <div className="text-2xl font-bold text-rose-300 tabular-nums" data-testid="portal-blacklisted">
                {blacklisted}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-rose-400/80">Blacklisted</div>
            </div>
          )}
          {unpaidTotal > 0 && (
            <div className="text-right hidden sm:block" data-testid="portal-unpaid">
              <div className="text-2xl font-bold text-amber-300 tabular-nums">
                ₹{Math.round(unpaidTotal).toLocaleString("en-IN")}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-amber-400/80">Unpaid dues</div>
            </div>
          )}
          <ChevronRight className="h-5 w-5 text-emerald-300 opacity-70 group-hover:translate-x-0.5 transition" />
        </div>
      </button>

      <PlayersPortalModal
        open={open}
        onClose={() => { setOpen(false); refresh(); }}
      />
    </>
  );
}
